// =============================================================================
// UTILIDADES DE FORMATEO - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Funciones centralizadas para formateo de monedas, fechas y números
// con localización chilena (es-CL)

// =============================================================================
// CONFIGURACIÓN DE LOCALIZACIÓN
// =============================================================================

const LOCALE = 'es-CL';
const TIMEZONE = 'America/Santiago';

// =============================================================================
// FORMATEO DE MONEDAS
// =============================================================================

/**
 * Formatea un monto en pesos chilenos (CLP)
 * @param {number|string} amount - Monto a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Monto formateado
 */
export const formatCLP = (amount, options = {}) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) return '$0';
  
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options
  }).format(numericAmount);
};

/**
 * Formatea un monto en Unidades de Fomento (UF)
 * @param {number|string} amount - Monto a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Monto formateado
 */
export const formatUF = (amount, options = {}) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) return 'UF 0';
  
  const formatted = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
    ...options
  }).format(numericAmount);
  
  return `UF ${formatted}`;
};

/**
 * Formatea un monto genérico con separadores de miles
 * @param {number|string} amount - Monto a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Monto formateado
 */
export const formatNumber = (amount, options = {}) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) return '0';
  
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options
  }).format(numericAmount);
};

// =============================================================================
// FORMATEO DE FECHAS
// =============================================================================

/**
 * Formatea una fecha en formato chileno (dd/mm/yyyy)
 * @param {string|Date} dateInput - Fecha a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Fecha formateada
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return 'N/A';

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) return 'N/A';
  
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TIMEZONE,
    ...options
  }).format(date);
};

/**
 * Formatea una fecha con hora en formato chileno
 * @param {string|Date} dateInput - Fecha a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Fecha y hora formateada
 */
export const formatDateTime = (dateInput, options = {}) => {
  if (!dateInput) return 'N/A';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return 'Fecha inválida';
  
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
    ...options
  }).format(date);
};

/**
 * Formatea una fecha en formato largo (ej: "15 de enero de 2024")
 * @param {string|Date} dateInput - Fecha a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Fecha formateada en formato largo
 */
export const formatDateLong = (dateInput, options = {}) => {
  if (!dateInput) return 'N/A';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return 'Fecha inválida';
  
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TIMEZONE,
    ...options
  }).format(date);
};

/**
 * Formatea una fecha en formato corto (ej: "15 ene 2024")
 * @param {string|Date} dateInput - Fecha a formatear
 * @param {Object} options - Opciones adicionales de formateo
 * @returns {string} Fecha formateada en formato corto
 */
export const formatDateShort = (dateInput, options = {}) => {
  if (!dateInput) return 'N/A';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return 'Fecha inválida';
  
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: TIMEZONE,
    ...options
  }).format(date);
};

// =============================================================================
// UTILIDADES ADICIONALES
// =============================================================================

/**
 * Formatea un RUT chileno
 * @param {string} rut - RUT a formatear
 * @returns {string} RUT formateado
 */
/**
 * Convierte un timestamp a fecha ISO (yyyy-mm-dd) según la zona horaria chilena.
 * Útil para comparar/agrupar por día local sin el corrimiento de UTC.
 * @param {string|Date} dateInput - Fecha a convertir
 * @returns {string} Fecha ISO local ('' si es inválida)
 */
export const toISODateChile = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  // en-CA produce yyyy-mm-dd
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE }).format(date);
};

/**
 * Fecha actual en formato ISO (yyyy-mm-dd) según la zona horaria chilena
 * @returns {string} Fecha ISO local de hoy
 */
export const todayISOChile = () => toISODateChile(new Date());

export const formatRUT = (rut) => {
  if (!rut) return '';
  
  // Remover puntos y guiones existentes
  const cleanRut = rut.toString().replace(/[^0-9kK]/g, '');
  
  if (cleanRut.length < 2) return cleanRut;
  
  // Separar número y dígito verificador
  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1).toUpperCase();
  
  // Formatear con puntos
  const formattedBody = body.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  
  return `${formattedBody}-${dv}`;
};

/**
 * Formatea un número de teléfono chileno
 * @param {string} phone - Teléfono a formatear
 * @returns {string} Teléfono formateado
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  // Remover caracteres no numéricos excepto el +
  const cleanPhone = phone.toString().replace(/[^0-9+]/g, '');
  
  // Si ya tiene código de país
  if (cleanPhone.startsWith('+56')) {
    const number = cleanPhone.substring(3);
    if (number.length === 9) {
      return `+56 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5)}`;
    }
  }
  
  // Si es un número de 9 dígitos (móvil chileno)
  if (cleanPhone.length === 9 && cleanPhone.startsWith('9')) {
    return `+56 ${cleanPhone.substring(0, 1)} ${cleanPhone.substring(1, 5)} ${cleanPhone.substring(5)}`;
  }
  
  return phone;
};

// =============================================================================
// UTILIDADES PARA VALORES COMPATIBLES CON <input type="datetime-local">
// =============================================================================
// Archivo: formatters.js
export const toLocalDateTimeInput = (dateInput) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "00";
  const yyyy = get('year');
  const mm = get('month');
  const dd = get('day');
  const hh = get('hour');
  const min = get('minute');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

export const toLocalMidnightInput = (dateInput) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "00";
  const yyyy = get('year');
  const mm = get('month');
  const dd = get('day');
  return `${yyyy}-${mm}-${dd}T00:00`;
};

export const toFileMakerDateTime = (dateTimeInput) => {
  if (!dateTimeInput) return "";
  const raw = String(dateTimeInput).trim();
  if (!raw) return "";

  const fileMakerLike = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(am|pm|a\.m\.|p\.m\.)$/i
  );
  if (fileMakerLike) {
    const dd = fileMakerLike[1];
    const mm = fileMakerLike[2];
    const yyyy = fileMakerLike[3];
    const hh12Raw = fileMakerLike[4];
    const min = fileMakerLike[5];
    const sec = fileMakerLike[6] || "00";
    const ampmRaw = String(fileMakerLike[7] || "").toLowerCase();
    const hh12 = Number(hh12Raw);
    if (!Number.isFinite(hh12)) return "";
    const hh = String(hh12);
    const ss = String(sec).padStart(2, "0");
    const ampm = ampmRaw.startsWith("p") ? "PM" : "AM";
    const includeSeconds = fileMakerLike[6] != null && ss !== "00";
    return includeSeconds
      ? `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss} ${ampm}`
      : `${dd}/${mm}/${yyyy} ${hh}:${min} ${ampm}`;
  }

  const m = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return "";

  const yyyy = m[1];
  const mm = m[2];
  const dd = m[3];
  const hhPart = m[4];
  const min = m[5];
  const sec = m[6] || "00";
  if (hhPart == null || min == null) return "";

  const hh24 = Number(hhPart);
  if (!Number.isFinite(hh24)) return "";

  const isPm = hh24 >= 12;
  let hh12 = hh24 % 12;
  if (hh12 === 0) hh12 = 12;

  const ampm = isPm ? "PM" : "AM";
  const ss = String(sec).padStart(2, "0");

  const includeSeconds = m[6] != null && ss !== "00";
  return includeSeconds
    ? `${dd}/${mm}/${yyyy} ${hh12}:${min}:${ss} ${ampm}`
    : `${dd}/${mm}/${yyyy} ${hh12}:${min} ${ampm}`;
};

export const toFileMakerDateTimeUS24 = (dateTimeInput) => {
  if (!dateTimeInput) return "";
  const raw = String(dateTimeInput).trim();
  if (!raw) return "";

  const us24 = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (us24) {
    const mm = us24[1];
    const dd = us24[2];
    const yyyy = us24[3];
    const hh = us24[4];
    const min = us24[5];
    const ss = String(us24[6] || "00").padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
  }

  const m = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return "";

  const yyyy = m[1];
  const mm = m[2];
  const dd = m[3];
  const hh = m[4];
  const min = m[5];
  const ss = String(m[6] || "00").padStart(2, "0");
  if (hh == null || min == null) return "";

  return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
};

export const toLocalDateTimeInputFromFileMaker = (dateTimeInput) => {
  if (!dateTimeInput) return "";
  const raw = String(dateTimeInput).trim();
  if (!raw) return "";

  const inputMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (inputMatch) {
    return `${inputMatch[1]}-${inputMatch[2]}-${inputMatch[3]}T${inputMatch[4]}:${inputMatch[5]}`;
  }

  const isoLike = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (isoLike) {
    return `${isoLike[1]}-${isoLike[2]}-${isoLike[3]}T${isoLike[4]}:${isoLike[5]}`;
  }

  const us24 = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (us24) {
    const mm = us24[1];
    const dd = us24[2];
    const yyyy = us24[3];
    const hh = us24[4];
    const min = us24[5];
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  const fm = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+([ap])\s*\.?\s*m\s*\.?$/i
  );
  if (!fm) return "";

  const dd = fm[1];
  const mm = fm[2];
  const yyyy = fm[3];
  const hh12 = Number(fm[4]);
  const min = fm[5];
  const ap = String(fm[7] || "").toLowerCase();
  if (!Number.isFinite(hh12)) return "";

  let hh24 = hh12 % 12;
  if (ap === "p") hh24 += 12;

  const hh = String(hh24).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

// =============================================================================
// EXPORTACIÓN PRINCIPAL
// =============================================================================
const formatters = {
  // Monedas
  formatCLP,
  formatUF,
  formatNumber,
  // Fechas
  formatDate,
  formatDateTime,
  formatDateLong,
  formatDateShort,
  toISODateChile,
  todayISOChile,
  // Nuevas utilidades para inputs datetime-local
  toLocalDateTimeInput,
  toLocalMidnightInput,
  toFileMakerDateTime,
  toFileMakerDateTimeUS24,
  toLocalDateTimeInputFromFileMaker,
  // Utilidades
  formatRUT,
  formatPhone,
  // Configuración
  LOCALE,
  TIMEZONE
};

export default formatters;
